import type { ContactBillingAddress } from '@api/contacts';

// Sentinel id for a billing address synthesized from a contact's legacy
// `billing_address`/`billing_postal_code` fields. GRAIN-1906 introduced the
// contact_billing_addresses table, but there is no full backfill: a row only
// appears once a contact's address + postal_code both change (see
// Contact#update_billing_address_records). Legacy-only contacts therefore
// serialize with `billing_addresses: []` while still carrying an address, and
// would otherwise render a blank billing field. Negative so it never collides
// with a real contact_billing_addresses join id.
export const LEGACY_BILLING_ADDRESS_ID = -1;

export interface LegacyBillingSource {
  address?: string | null;
  postalCode?: string | null;
}

// Returns the saved billing-address rows, or a single synthesized entry from
// the contact's legacy address when there are no saved rows — so legacy-only
// contacts keep prefilling instead of showing blank.
export function withLegacyBillingFallback(
  addresses: ContactBillingAddress[],
  legacy: LegacyBillingSource | null | undefined,
): ContactBillingAddress[] {
  if (addresses.length > 0) return addresses;

  const address = legacy?.address?.trim();
  if (!address) return addresses;

  return [
    {
      id: LEGACY_BILLING_ADDRESS_ID,
      name: null,
      address,
      postal_code: legacy?.postalCode?.trim() ?? '',
      is_default: true,
    },
  ];
}

// API returns default-first, but don't rely on ordering.
export function defaultBillingAddress(addresses: ContactBillingAddress[]): ContactBillingAddress | null {
  return addresses.find((address) => address.is_default) ?? addresses[0] ?? null;
}

// The synthesized legacy entry has no row behind it, so it can never be stored
// on an order.
export function savedBillingAddressId(id: number | null | undefined): number | null {
  return id != null && id > 0 ? id : null;
}

// …and, for the same reason, can't be edited: there is nothing to write back to.
export function isSavedBillingAddress(
  billingAddress: ContactBillingAddress | null | undefined,
): billingAddress is ContactBillingAddress {
  return billingAddress != null && savedBillingAddressId(billingAddress.id) != null;
}
